import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/adminApi';
import type { FeedbackAnalysis } from '../../services/adminApi';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  FaceSmileIcon,
  FaceFrownIcon,
  MinusCircleIcon,
  StarIcon,
  EyeIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { SectionPerformanceChart } from '../../components/charts/SectionPerformanceChart';
import { RatingDistributionChart } from '../../components/charts/RatingDistributionChart';
import { FeedbackTrendsChart } from '../../components/charts/FeedbackTrendsChart';
import { ImprovementAreasRanking } from '../../components/charts/ImprovementAreasRanking';

const StatCard: React.FC<{ title: string; value: string; icon: React.ComponentType<any> }> = ({ title, value, icon: Icon }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <Icon className="h-8 w-8 text-blue-600" />
      </div>
      <div className="ml-5 w-0 flex-1">
        <dl>
          <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
          <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
        </dl>
      </div>
    </div>
  </div>
);

export default function FeedbackAnalysisPage() {
  const [analysis, setAnalysis] = useState<FeedbackAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'detailed'>('overview');

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getFeedbackAnalysis();
        setAnalysis(response.analysis);
      } catch (err) {
        setError('Erro ao carregar análise de feedbacks.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, []);

  if (loading) {
    return <div className="text-center p-8">Carregando análise...</div>;
  }

  if (error) {
    return <div className="rounded-md bg-red-50 p-4 text-red-700">{error}</div>;
  }

  if (!analysis) {
    return <div className="text-center p-8">Nenhum dado de análise encontrado.</div>;
  }

  // Dados mockados para demonstração das tendências temporais (em produção viria do backend)
  const mockTrendsData = [
    { date: '2025-01-01', overall_rating: 4.2, accuracy_rating: 4.0, satisfaction_rating: 4.1, feedback_count: 15 },
    { date: '2025-01-02', overall_rating: 4.3, accuracy_rating: 4.1, satisfaction_rating: 4.2, feedback_count: 12 },
    { date: '2025-01-03', overall_rating: 4.4, accuracy_rating: 4.2, satisfaction_rating: 4.3, feedback_count: 18 },
    { date: '2025-01-04', overall_rating: 4.5, accuracy_rating: 4.3, satisfaction_rating: 4.4, feedback_count: 20 },
    { date: '2025-01-05', overall_rating: 4.4, accuracy_rating: 4.2, satisfaction_rating: 4.3, feedback_count: 16 },
    { date: '2025-01-06', overall_rating: 4.6, accuracy_rating: 4.4, satisfaction_rating: 4.5, feedback_count: 22 },
    { date: '2025-01-07', overall_rating: 4.5, accuracy_rating: 4.3, satisfaction_rating: 4.4, feedback_count: 19 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/dashboard" className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Voltar para o Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Análise de Feedbacks</h1>
            <p className="mt-1 text-sm text-gray-500">
              Insights detalhados sobre a satisfação dos usuários - FASE 3 🚀
            </p>
          </div>
          
          {/* Toggle de visualização */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('overview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'overview'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <EyeIcon className="w-4 h-4" />
              Visão Geral
            </button>
            <button
              onClick={() => setViewMode('detailed')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'detailed'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              Análise Detalhada
            </button>
          </div>
        </div>
      </div>

      {/* Métricas principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total de Feedbacks" value={analysis.total_feedbacks.toString()} icon={ChatBubbleBottomCenterTextIcon} />
        <StatCard title="Avaliação Geral" value={`${analysis.average_overall_rating.toFixed(1)}/5`} icon={StarIcon} />
        <StatCard title="Precisão Média" value={`${analysis.average_accuracy_rating.toFixed(1)}/5`} icon={StarIcon} />
        <StatCard title="Satisfação Média" value={`${analysis.average_satisfaction_rating.toFixed(1)}/5`} icon={StarIcon} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard title="Taxa de Recomendação" value={`${analysis.recommendation_rate.toFixed(1)}%`} icon={ChartBarIcon} />
        <StatCard title="Último Feedback" value={new Date(analysis.most_recent_feedback_at).toLocaleDateString()} icon={ChartBarIcon} />
        <StatCard title="Comentários Recentes" value={analysis.recent_comments.length.toString()} icon={ChatBubbleBottomCenterTextIcon} />
      </div>

      {/* Conteúdo baseado no modo de visualização */}
      {viewMode === 'overview' ? (
        // Visão Geral - Gráficos principais em grid
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de Performance por Seção */}
          <SectionPerformanceChart 
            sectionRatings={analysis.section_ratings}
            className="lg:col-span-2"
          />
          
          {/* Gráfico de Distribuição de Ratings */}
          <RatingDistributionChart 
            ratingDistribution={analysis.rating_distribution}
          />
          
          {/* Ranking de Áreas de Melhoria */}
          <ImprovementAreasRanking 
            improvementAreas={analysis.common_improvement_areas}
            totalFeedbacks={analysis.total_feedbacks}
          />
        </div>
      ) : (
        // Análise Detalhada - Layout mais completo
        <div className="space-y-6">
          {/* Gráfico de Tendências */}
          <FeedbackTrendsChart 
            trendsData={mockTrendsData}
            className="w-full"
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Performance por Seção */}
            <SectionPerformanceChart 
              sectionRatings={analysis.section_ratings}
              className="lg:col-span-2"
            />
            
            {/* Distribuição de Ratings */}
            <RatingDistributionChart 
              ratingDistribution={analysis.rating_distribution}
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ranking de Melhorias */}
            <ImprovementAreasRanking 
              improvementAreas={analysis.common_improvement_areas}
              totalFeedbacks={analysis.total_feedbacks}
            />
            
            {/* Análise de Sentimento e Categorias - Layout tradicional */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Análise de Sentimento</h3>
                <div className="space-y-4">
                  <div className="flex items-center"><FaceSmileIcon className="h-6 w-6 text-green-500 mr-2"/> Positivos: {analysis.sentiment.positive}</div>
                  <div className="flex items-center"><MinusCircleIcon className="h-6 w-6 text-yellow-500 mr-2"/> Neutros: {analysis.sentiment.neutral}</div>
                  <div className="flex items-center"><FaceFrownIcon className="h-6 w-6 text-red-500 mr-2"/> Negativos: {analysis.sentiment.negative}</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Feedbacks por Categoria</h3>
                <div className="space-y-3">
                  {Object.entries(analysis.feedbacks_by_category).map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <div className="font-medium capitalize">{category}</div>
                      <div className="text-gray-500">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comentários Recentes */}
      {analysis.recent_comments.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Comentários Recentes dos Usuários
          </h3>
          <div className="space-y-4">
            {analysis.recent_comments.map((comment) => (
              <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-r-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-gray-800 italic">"{comment.comment}"</p>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        <span>{comment.overall_rating}/5</span>
                      </div>
                      <div className="flex items-center">
                        {comment.would_recommend ? (
                          <>
                            <FaceSmileIcon className="h-4 w-4 text-green-500 mr-1" />
                            <span>Recomendaria</span>
                          </>
                        ) : (
                          <>
                            <FaceFrownIcon className="h-4 w-4 text-red-500 mr-1" />
                            <span>Não recomendaria</span>
                          </>
                        )}
                      </div>
                      <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seções com melhor performance */}
      {Object.keys(analysis.section_ratings).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Performance por Seção do Relatório
          </h3>
          <div className="space-y-3">
            {Object.entries(analysis.section_ratings)
              .sort(([,a], [,b]) => b - a)
              .map(([section, rating]) => (
                <div key={section} className="flex items-center justify-between">
                  <div className="font-medium capitalize">{section.replace('_', ' ')}</div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">{rating.toFixed(1)}/5</span>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(rating / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Áreas de melhoria mais solicitadas */}
      {analysis.common_improvement_areas.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Áreas de Melhoria Mais Solicitadas
          </h3>
          <div className="space-y-3">
            {analysis.common_improvement_areas
              .sort((a, b) => b.count - a.count)
              .map((area) => (
                <div key={area.area} className="flex items-center justify-between">
                  <div className="font-medium">{area.area}</div>
                  <div className="text-gray-500">{area.count} menções</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
} 